import { Box, Typography, useTheme } from '@mui/material'
import React, { memo, useMemo } from 'react'
import { TradingSession } from '../../proto/generated/trading-calendar'
import { formatEpochTime } from '../../util/dateTime'
import { marketStatusBlue, marketStatusCas, marketStatusGreen, marketStatusOrange } from '../../assets/colors'
import { NonInteractiveTooltip } from '../../components/custom'

const DAY_MINUTES = 24 * 60
const BAR_HEIGHT = 6
const SHORT_THRESHOLD_PERCENT = 2.5
const SHORT_BUFFER_PERCENT = 3.5
const SEGMENT_GAP_PERCENT = 0.3

export interface MarketStatusBarProps {
    tradingSession: TradingSession
}

const minutesOfDay = (epoch: number) => {
    const date = new Date(epoch)
    return date.getHours() * 60 + date.getMinutes()
}

const toPercent = (epoch: number) => (minutesOfDay(epoch) / DAY_MINUTES) * 100

/** A session window is drawable only when both bounds are set and end is after start. */
const hasValidWindow = (start: number, end: number) => !!(start && end && end > start)

const weekDay = (epoch: number) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    return days[new Date(epoch).getDay()].toUpperCase()
}

interface RawSegment {
    key: string
    color: string
    left: number
    right: number
    text: string
    zIndex?: number
    buffer?: boolean
}

interface LaidOutSegment extends RawSegment {
    renderLeft: number
    renderWidth: number
}

const layoutWithBuffers = (raw: RawSegment[]): LaidOutSegment[] => {
    const items = raw
        .filter((s) => s.right > s.left)
        .map((s) => {
            const natural = s.right - s.left
            let left = s.left
            let right = s.right
            if (s.buffer && natural < SHORT_THRESHOLD_PERCENT) {
                const mid = (s.left + s.right) / 2
                left = mid - SHORT_BUFFER_PERCENT / 2
                right = mid + SHORT_BUFFER_PERCENT / 2
                if (left < 0) {
                    right -= left
                    left = 0
                }
                if (right > 100) {
                    left -= right - 100
                    right = 100
                }
            }
            return { ...s, left, right }
        })
        .sort((a, b) => a.left - b.left)

    for (let i = 0; i < items.length - 1; i++) {
        const a = items[i]
        const b = items[i + 1]
        const overlap = a.right + SEGMENT_GAP_PERCENT - b.left
        if (overlap <= 0) continue

        if (!a.buffer && b.buffer) {
            a.right -= overlap
        } else if (a.buffer && !b.buffer) {
            b.left += overlap
        } else {
            a.right -= overlap / 2
            b.left += overlap / 2
        }
    }

    return items
        .map((s) => ({
            ...s,
            renderLeft: s.left,
            renderWidth: Math.max(0, s.right - s.left)
        }))
        .filter((s) => s.renderWidth > 0)
}

interface SegmentProps {
    color: string
    left: number
    width: number
    text: string
    zIndex?: number
}

const Segment = memo(({ color, left, width, text, zIndex }: SegmentProps) => {
    if (width <= 0) return null

    return (
        <div
            style={{
                position: 'absolute',
                left: `${left}%`,
                width: `${width}%`,
                height: `${BAR_HEIGHT}px`,
                zIndex: zIndex ?? 0
            }}
        >
            <NonInteractiveTooltip title={text} arrow placement="top">
                <div
                    style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: color,
                        borderRadius: `${BAR_HEIGHT / 2}px`,
                        cursor: 'default'
                    }}
                />
            </NonInteractiveTooltip>
        </div>
    )
})

Segment.displayName = 'Segment'

/** Split/clip `post` so it never covers the CAS window (they often share 15:40–16:00). */
const postRangesExcludingCas = (
    postLeft: number,
    postRight: number,
    casLeft: number,
    casRight: number,
    hasCas: boolean
): Array<{ left: number; width: number }> => {
    if (postRight <= postLeft) return []
    if (!hasCas || casRight <= casLeft) {
        return [{ left: postLeft, width: postRight - postLeft }]
    }
    if (postRight <= casLeft || postLeft >= casRight) {
        return [{ left: postLeft, width: postRight - postLeft }]
    }
    const ranges: Array<{ left: number; width: number }> = []
    if (postLeft < casLeft) {
        ranges.push({ left: postLeft, width: casLeft - postLeft })
    }
    if (postRight > casRight) {
        ranges.push({ left: casRight, width: postRight - casRight })
    }
    return ranges
}

const MarketStatusBar = (props: MarketStatusBarProps) => {
    const theme = useTheme()
    const tradingSession = props.tradingSession

    const hasPre = hasValidWindow(tradingSession.preOpenStartTime, tradingSession.preOpenEndTime)
    const hasOpen = hasValidWindow(tradingSession.startTime, tradingSession.endTime)
    const hasCas = hasValidWindow(tradingSession.casStartTime, tradingSession.casEndTime)
    const hasPost = hasValidWindow(tradingSession.postCloseStartTime, tradingSession.postCloseEndTime)

    const preLeft = hasPre ? toPercent(tradingSession.preOpenStartTime) : 0
    const preRight = hasPre ? toPercent(tradingSession.preOpenEndTime) : 0
    const openLeft = hasOpen ? toPercent(tradingSession.startTime) : 0
    const openRight = hasOpen ? toPercent(tradingSession.endTime) : 0
    const casLeft = hasCas ? toPercent(tradingSession.casStartTime) : 0
    const casRight = hasCas ? toPercent(tradingSession.casEndTime) : 0
    const postLeft = hasPost ? toPercent(tradingSession.postCloseStartTime) : 0
    const postRight = hasPost ? toPercent(tradingSession.postCloseEndTime) : 0

    const postRanges = useMemo(
        () => hasPost ? postRangesExcludingCas(postLeft, postRight, casLeft, casRight, hasCas) : [],
        [hasPost, postLeft, postRight, casLeft, casRight, hasCas]
    )

    const trackColor = theme.palette.mode === 'dark' ? '#363a45' : theme.palette.divider

    const segments = useMemo(() => {
        const raw: RawSegment[] = []
        if (hasPre) {
            raw.push({
                key: 'pre',
                color: marketStatusOrange,
                left: preLeft,
                right: preRight,
                buffer: true,
                zIndex: 1,
                text: `Pre-market ${formatEpochTime(tradingSession.preOpenStartTime)} - ${formatEpochTime(tradingSession.preOpenEndTime)}`
            })
        }
        if (hasOpen) {
            raw.push({
                key: 'open',
                color: marketStatusGreen,
                left: openLeft,
                right: openRight,
                text: `Market open ${formatEpochTime(tradingSession.startTime)} - ${formatEpochTime(tradingSession.endTime)}`
            })
        }
        if (hasCas) {
            raw.push({
                key: 'cas',
                color: marketStatusCas,
                left: casLeft,
                right: casRight,
                buffer: true,
                zIndex: 2,
                text: `Closing auction ${formatEpochTime(tradingSession.casStartTime)} - ${formatEpochTime(tradingSession.casEndTime)}`
            })
        }
        postRanges.forEach((range, index) => {
            raw.push({
                key: `post-${index}`,
                color: marketStatusBlue,
                left: range.left,
                right: range.left + range.width,
                buffer: true,
                zIndex: 1,
                text: `Post-market ${formatEpochTime(tradingSession.postCloseStartTime)} - ${formatEpochTime(tradingSession.postCloseEndTime)}`
            })
        })
        const laidOut = layoutWithBuffers(raw)
        // Closed wings on both sides — tooltip uses real session bounds (00:00 / 24:00).
        const closedStartEpoch = hasPre
            ? tradingSession.preOpenStartTime
            : hasOpen
                ? tradingSession.startTime
                : hasCas
                    ? tradingSession.casStartTime
                    : hasPost
                        ? tradingSession.postCloseStartTime
                        : 0
        const closedEndEpoch = hasPost
            ? tradingSession.postCloseEndTime
            : hasCas
                ? tradingSession.casEndTime
                : hasOpen
                    ? tradingSession.endTime
                    : hasPre
                        ? tradingSession.preOpenEndTime
                        : 0

        if (!laidOut.length) {
            return [{
                key: 'closed',
                color: trackColor,
                left: 0,
                right: 100,
                renderLeft: 0,
                renderWidth: 100,
                text: 'Market closed 00:00 - 24:00',
                zIndex: 0
            } satisfies LaidOutSegment]
        }
        const activityStart = Math.min(...laidOut.map((s) => s.renderLeft))
        const activityEnd = Math.max(...laidOut.map((s) => s.renderLeft + s.renderWidth))
        const closed: LaidOutSegment[] = []
        if (activityStart > 0 && closedStartEpoch) {
            closed.push({
                key: 'closed-left',
                color: trackColor,
                left: 0,
                right: activityStart,
                renderLeft: 0,
                renderWidth: activityStart,
                text: `Market closed 00:00 - ${formatEpochTime(closedStartEpoch)}`,
                zIndex: 0
            })
        }
        if (activityEnd < 100 && closedEndEpoch) {
            closed.push({
                key: 'closed-right',
                color: trackColor,
                left: activityEnd,
                right: 100,
                renderLeft: activityEnd,
                renderWidth: 100 - activityEnd,
                text: `Market closed ${formatEpochTime(closedEndEpoch)} - 24:00`,
                zIndex: 0
            })
        }
        return [...closed, ...laidOut]
    }, [
        hasPre, hasOpen, hasCas, postRanges,
        preLeft, preRight, openLeft, openRight, casLeft, casRight,
        trackColor,
        tradingSession.preOpenStartTime, tradingSession.preOpenEndTime,
        tradingSession.startTime, tradingSession.endTime,
        tradingSession.casStartTime, tradingSession.casEndTime,
        tradingSession.postCloseStartTime, tradingSession.postCloseEndTime
    ])

    const now = new Date()
    const nowPercent = ((now.getHours() * 60 + now.getMinutes()) / DAY_MINUTES) * 100

    // Labels sit under the open and close markers (TradingView-style), not 00:00 / 24:00.
    const labelStart = hasOpen ? openLeft : hasPre ? preLeft : hasCas ? casLeft : hasPost ? postLeft : 0
    const labelEnd = hasCas ? casLeft : hasPost ? postLeft : hasOpen ? openRight : hasPre ? preRight : 0
    const labelStartText = hasOpen
        ? formatEpochTime(tradingSession.startTime)
        : hasPre
            ? formatEpochTime(tradingSession.preOpenStartTime)
            : hasCas
                ? formatEpochTime(tradingSession.casStartTime)
                : hasPost
                    ? formatEpochTime(tradingSession.postCloseStartTime)
                    : ''
    const labelEndText = hasCas
        ? formatEpochTime(tradingSession.casStartTime)
        : hasPost
            ? formatEpochTime(tradingSession.postCloseStartTime)
            : hasOpen
                ? formatEpochTime(tradingSession.endTime)
                : hasPre
                    ? formatEpochTime(tradingSession.preOpenEndTime)
                    : ''

    return (
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <Typography
                variant={'caption'}
                sx={{
                    lineHeight: `${BAR_HEIGHT}px`,
                    color: 'text.secondary',
                    fontSize: '11px',
                    letterSpacing: '0.02em',
                    pt: '0px'
                }}
            >
                {weekDay(tradingSession.startTime)}
            </Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ position: 'relative', width: '100%', height: `${BAR_HEIGHT}px` }}>
                    <div style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: 0,
                        height: `${BAR_HEIGHT}px`,
                        borderRadius: `${BAR_HEIGHT / 2}px`,
                        backgroundColor: trackColor,
                        pointerEvents: 'none'
                    }} />
                    {segments.map((segment) => (
                        <Segment
                            key={segment.key}
                            color={segment.color}
                            left={segment.renderLeft}
                            width={segment.renderWidth}
                            zIndex={segment.zIndex}
                            text={segment.text}
                        />
                    ))}
                    <div style={{
                        position: 'absolute',
                        left: `${nowPercent}%`,
                        top: '-4px',
                        width: '2px',
                        height: `${BAR_HEIGHT + 8}px`,
                        borderRadius: '1px',
                        backgroundColor: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
                        transform: 'translateX(-50%)',
                        boxShadow: theme.palette.mode === 'dark' ? '0 0 0 1px rgba(0,0,0,0.35)' : undefined,
                        zIndex: 3,
                        pointerEvents: 'none'
                    }} />
                </Box>
                {(labelStartText || labelEndText) && (
                    <Box sx={{ position: 'relative', height: '16px', marginTop: '6px' }}>
                        {labelStartText && (
                            <Typography
                                variant={'caption'}
                                sx={{
                                    position: 'absolute',
                                    left: `${labelStart}%`,
                                    transform: 'translateX(-50%)',
                                    color: 'text.secondary',
                                    fontSize: '11px',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {labelStartText}
                            </Typography>
                        )}
                        {labelEndText && (
                            <Typography
                                variant={'caption'}
                                sx={{
                                    position: 'absolute',
                                    left: `${labelEnd}%`,
                                    transform: 'translateX(-50%)',
                                    color: 'text.secondary',
                                    fontSize: '11px',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {labelEndText}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    )
}


export default memo(MarketStatusBar)
